import type { User } from '@/@types/user'
import { css } from '@emotion/react'
import { type HTMLMotionProps, motion } from 'motion/react'
import { forwardRef, ImgHTMLAttributes } from 'react'

export interface MentionUserProps extends HTMLMotionProps<'div'> {
	data: User
}

// 处理用户头像的子组件
function UserAvatar({ src }: Pick<ImgHTMLAttributes<HTMLImageElement>, 'src'>) {
	return (
		<img
			width={18}
			height={18}
			src={src}
			css={css`
				width: 18px;
				height: 18px;
				border-radius: 18px;
				border: 1px solid var(--solid-separator);
			`}
		/>
	)
}

// 处理用户名的子组件
function UserName({ name }: { name: string }) {
	return (
		<span
			css={css`
				color: var(--tint-jike-blue);
				font-size: 12px;
				font-weight: 500;
				line-height: 18px;
				letter-spacing: 0.25px;
				padding-right: 3px;
			`}
		>
			{name}
		</span>
	)
}

export default forwardRef<HTMLDivElement, MentionUserProps>(function MentionUser(
	{ data, ...props }: MentionUserProps,
	ref
) {
	return (
		<motion.div
			initial={{ opacity: 0 }}
			animate={{ opacity: 1 }}
			transition={{ duration: 0.2 }}
			{...props}
			tabIndex={0}
			ref={ref}
			css={css`
				display: inline-flex;
				padding: 3px 4px;
				margin: 0 2px;
				gap: 4px;
				align-items: center;
				white-space: nowrap;
				border-radius: 15px;
				background-color: var(--bg-on-body-2);
				transition: background-color 0.6s;
				position: relative;
				overflow: hidden;

				&::before {
					content: '';
					position: absolute;
					inset: 0;
					background-color: var(--bg-selected-blue);
					border-radius: inherit;
					transform: scaleX(0);
					opacity: 0;
					transition:
						transform 0.6s cubic-bezier(0, 0.55, 0.45, 1),
						opacity 0.3s;
				}

				&:hover,
				&:focus {
					&::before {
						opacity: 1;
						transform: scaleX(1);
					}
				}
			`}
		>
			<UserAvatar src={data.avatarImage.thumbnailUrl} />
			<UserName name={data.screenName} />
		</motion.div>
	)
})
